import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { HiArrowNarrowDown, HiArrowNarrowUp } from 'react-icons/hi';
import { MdHistory } from 'react-icons/md';
import listStyles from '../../general_components/content_management/list_view/listView.module.scss';
import { ListViewFooter } from '../../general_components/content_management/list_view/listViewFooter';
import { AddButton } from '../../general_components/content_management/reusableItems/buttons';
import { CheckBoxInput } from '../../general_components/content_management/reusableItems/checkboxInput';
import { ThreeDotsButton } from '../../general_components/content_management/reusableItems/buttons';
import { ConfirmationDialogue } from '../../general_components/forms/ConfirmationDialogue';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import { can } from '../../supplementary_components/userDataDelegate';
import { openActivitiesPopup } from '../../general_components/activitiesPopup/activitiesPopup';
import activitiesStyles from '../../general_components/activitiesPopup/activitiesPopup.module.scss';
import { ATTACHMENT_ENTITY_TYPES } from '../../general_components/attachmentsPopup/attachmentEntityConfig';
import { openAttachmentsPopup } from '../../general_components/attachmentsPopup/attachmentsPopup';
import { openAddAttachmentForm } from '../../general_components/attachmentsPopup/supplementaryFilesListing';
import { CalendarPicker, useCloseOnOutsidePress } from '../../general_components/content_management/reusableItems/calendar';
import { localDateToUSFormat } from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';
import { Form } from '../../general_components/forms/Form';
import { addNotification, addPopup } from '../../supplementary_components/store';
import { setCompany } from '../../supplementary_components/companySettings';
import { dateFormatter } from '../../supplementary_components/supplementaryMethods';
import { dateStringToLocalDateString, dateToLocalDateString } from '../reports/weeklyReports';
import { buildApplicantForm } from './applicantAddConf';
import { hiringApi, hiringErrorMessage } from './hiringApi';
import { PhoneLookup } from './phoneLookup';
import {
    columnTemplate,
    contactLabel,
    displayName,
    experienceLabel,
    fileCount,
    hiringColumns,
    HIRING_CATEGORIES,
    insuranceLabel,
    orderHiringRows,
    paymentLabel,
    rowsInCategory,
    statusLabel,
    typeLabel,
} from './hiringList';
import { formatIsoDate } from '../inspections/safetyApi';
import styles from './hiring.module.scss';

function accessibleCompanyCount() {
    try {
        const parsed = JSON.parse(localStorage.getItem('accessibleCompanies') || '[]');
        return Array.isArray(parsed) ? parsed.length : 0;
    } catch (_) {
        return 0;
    }
}

const STATUS_OPTIONS = [
    { value: 'NEW', label: 'New', tone: 'statusNew' },
    { value: 'PRE_HIRE_SENT', label: 'Pre-Hire Sent', tone: 'statusSent' },
    { value: 'PRE_HIRE_COMPLETED', label: 'Pre-Hire Completed', tone: 'statusDone' },
    { value: 'HIRED', label: 'Hired', tone: 'statusDone' },
    { value: 'REJECTED', label: 'Rejected', tone: 'statusRejected' },
];

const INSURANCE_OPTIONS = [
    { value: 'NOT_SENT', label: 'Not Sent', tone: 'insuranceNone' },
    { value: 'SENT', label: 'Sent', tone: 'insuranceSent' },
    { value: 'APPROVED', label: 'Approved', tone: 'insuranceApproved' },
    { value: 'REJECTED', label: 'Rejected', tone: 'insuranceRejected' },
];

function badgeTone(options, value) {
    return options.find((option) => option.value === value)?.tone || '';
}

function leadPayload(row, patch) {
    return {
        driverType: row.driverType,
        name: row.name,
        phoneNumber: row.phoneNumber,
        email: row.email,
        reference: row.reference,
        experienceYears: row.experienceYears,
        experienceMonths: row.experienceMonths,
        note: row.note,
        comingDate: row.comingDate,
        paymentType: row.paymentType,
        paymentAmount: row.paymentAmount,
        status: row.status,
        insuranceStatus: row.insuranceStatus,
        ...patch,
    };
}

function BadgeField({ row, field, options, canManage, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const [editorWidth, setEditorWidth] = useState(null);
    const saving = useRef(false);
    const openedAt = useRef(0);
    const selectRef = useRef(null);
    const current = row[field];
    const tone = styles[badgeTone(options, current)] || '';
    const label = options.find((option) => option.value === current)?.label || current || '-';
    if (!canManage) {
        return <span className={`${styles.badge} ${tone}`}>{label}</span>;
    }
    if (!editing) {
        return (
            <button
                type="button"
                className={`${styles.badge} ${tone}`}
                onClick={(event) => {
                    event.stopPropagation();
                    openedAt.current = Date.now();
                    const width = event.currentTarget.offsetWidth;
                    flushSync(() => {
                        setEditorWidth(width);
                        setEditing(true);
                    });
                    try {
                        selectRef.current?.showPicker();
                    } catch (_) {
                        selectRef.current?.focus();
                    }
                }}
            >
                {label}
            </button>
        );
    }
    return (
        <select
            ref={selectRef}
            className={`${styles.badge} ${styles.badgeSelect} ${tone}`}
            style={editorWidth ? { width: `${editorWidth}px` } : undefined}
            defaultValue={current}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
                if (event.key === 'Escape') setEditing(false);
            }}
            onChange={(event) => {
                const next = event.target.value;
                if (next === current) {
                    setEditing(false);
                    return;
                }
                saving.current = true;
                hiringApi.save(row.id, leadPayload(row, { [field]: next })).then((saved) => {
                    onUpdated?.(saved);
                    setEditing(false);
                }).catch((saveError) => {
                    addNotification({ message: hiringErrorMessage(saveError), type: 'alert' });
                }).finally(() => {
                    saving.current = false;
                });
            }}
            onBlur={() => {
                window.setTimeout(() => {
                    if (saving.current) return;
                    if (Date.now() - openedAt.current < 400) return;
                    setEditing(false);
                }, 200);
            }}
        >
            {options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </select>
    );
}

function parseIsoDate(iso) {
    if (!iso) return undefined;
    const [year, month, day] = String(iso).split('-').map((part) => Number(part));
    if (!year || !month || !day) return undefined;
    return new Date(year, month - 1, day);
}

function ComingDateEditor({ row, onUpdated, onClose }) {
    const shellRef = useRef(null);
    const inputRef = useRef(null);
    const popoverRef = useRef(null);
    const saving = useRef(false);
    const closed = useRef(false);
    const outsideRefs = useMemo(() => [shellRef, popoverRef], []);

    const close = () => {
        if (closed.current) return;
        closed.current = true;
        onClose();
    };

    const saveIso = (iso) => {
        if (closed.current || saving.current) return;
        const next = iso || null;
        if (next === (row.comingDate || null)) {
            close();
            return;
        }
        saving.current = true;
        hiringApi.save(row.id, leadPayload(row, { comingDate: next })).then((saved) => {
            onUpdated?.(saved);
            close();
        }).catch((saveError) => {
            saving.current = false;
            addNotification({ message: hiringErrorMessage(saveError), type: 'alert' });
        });
    };

    const leaveIfCleared = () => {
        if (closed.current || saving.current) return;
        const value = String(inputRef.current?.value || '').trim();
        if (!value) {
            saveIso(null);
            return;
        }
        close();
    };

    useCloseOnOutsidePress(true, outsideRefs, leaveIfCleared);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const commitTyped = (raw) => {
        const trimmed = String(raw || '').trim();
        if (!trimmed) {
            saveIso(null);
            return;
        }
        const formatted = dateFormatter(trimmed);
        if (!formatted.date) {
            addNotification({ message: 'Invalid date', type: 'alert' });
            return;
        }
        saveIso(dateStringToLocalDateString(formatted.date));
    };

    return (
        <div
            ref={shellRef}
            className={styles.dateEditor}
            onClick={(event) => event.stopPropagation()}
        >
            <input
                ref={inputRef}
                className={styles.dateInput}
                aria-label="Coming date"
                autoComplete="off"
                defaultValue={row.comingDate ? localDateToUSFormat(row.comingDate) : ''}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') close();
                    if (event.key === 'Enter') commitTyped(event.currentTarget.value);
                }}
                onBlur={(event) => {
                    if (popoverRef.current?.contains(event.relatedTarget)) return;
                    if (!String(event.currentTarget.value || '').trim()) saveIso(null);
                }}
            />
            <CalendarPicker
                selectType="singleDate"
                popoverAlign="start"
                anchorRef={shellRef}
                popoverRef={popoverRef}
                autoVerticalPlacement
                initialStart={parseIsoDate(row.comingDate)}
                onCancel={close}
                onApply={(date) => saveIso(dateToLocalDateString(date))}
            />
        </div>
    );
}

function ComingDateField({ row, canManage, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const label = formatIsoDate(row.comingDate);
    if (!canManage) return label;
    if (!editing) {
        return (
            <button
                type="button"
                className={styles.inlineValue}
                aria-label={`Coming date, ${label}`}
                onClick={(event) => {
                    event.stopPropagation();
                    setEditing(true);
                }}
            >
                {label}
            </button>
        );
    }
    return (
        <ComingDateEditor
            row={row}
            onUpdated={onUpdated}
            onClose={() => setEditing(false)}
        />
    );
}

function FilesField({ row, canManage, onUpdated }) {
    const count = fileCount(row);
    const open = (event) => {
        event.stopPropagation();
        openAttachmentsPopup({
            entityType: ATTACHMENT_ENTITY_TYPES.POTENTIAL_DRIVER,
            entity: row,
            titleLabel: row.name || '',
            canEdit: canManage,
            onEntityUpdated: onUpdated,
        });
    };
    if (!count && !canManage) return '-';
    if (!count) {
        return (
            <div className={styles.fileEmpty}>
                <span className={styles.fileDash}>-</span>
                <span className={styles.fileAdd} aria-label="Add file">
                    <AddButton
                        customSize="1.6rem"
                        action={(event) => {
                            event.stopPropagation();
                            openAddAttachmentForm({
                                entityType: ATTACHMENT_ENTITY_TYPES.POTENTIAL_DRIVER,
                                entityId: row.id,
                                onUploaded: onUpdated,
                            });
                        }}
                    />
                </span>
            </div>
        );
    }
    return (
        <button type="button" className={styles.fileLink} onClick={open}>
            See Files
        </button>
    );
}

function cellText(row, key, wrapped) {
    switch (key) {
        case 'driverType': return typeLabel(row.driverType);
        case 'payment': return paymentLabel(row);
        case 'name': return displayName(row, wrapped);
        case 'contact': return contactLabel(row);
        case 'reference': return row.reference || '-';
        case 'experience': return experienceLabel(row);
        case 'note': return row.note || '-';
        case 'insuranceStatus': return insuranceLabel(row.insuranceStatus);
        case 'status': return statusLabel(row.status);
        case 'comingDate': return formatIsoDate(row.comingDate);
        case 'companyName': return row.companyName || '-';
        case 'files': return fileCount(row) ? 'See Files' : '-';
        default: return '-';
    }
}

function nextSelection(selectedList, checked, id, rowCount) {
    let next = [...selectedList];
    if (selectedList.length > 0 && selectedList[0] === 'selectAll') {
        next[0] = 'ignoreFollowing';
        next.push(id);
    } else if (selectedList.length > 0 && selectedList[0] !== 'ignoreFollowing' && checked) {
        next.push(id);
    } else if (selectedList.length > 0 && selectedList[0] === 'ignoreFollowing' && !checked) {
        next.push(id);
    } else if (next.length > 0 && next[0] === 'ignoreFollowing' && checked) {
        next = next.filter((item) => item !== id);
        if (next.length === 1) next = ['selectAll'];
    } else if (checked) {
        next.push(id);
    } else {
        next = next.filter((item) => item !== id);
    }
    return rowCount < next.length ? [] : next;
}

export function HiringPage() {
    const canView = can('HIRING', 'VIEW');
    const canManage = can('HIRING', 'MANAGE');
    const canViewDrivers = can('DRIVERS', 'VIEW');
    const navigate = useNavigate();
    const showCompany = accessibleCompanyCount() > 1;
    const columns = hiringColumns(showCompany);
    const template = columnTemplate(showCompany);
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    const [sort, setSort] = useState(null);
    const [category, setCategory] = useState('IN_PROGRESS');
    const [selected, setSelected] = useState([]);
    const [pagination, setPagination] = useState({ currentPageNumber: 0 });
    const [reloadToken, setReloadToken] = useState(0);
    const [lookupOpen, setLookupOpen] = useState(false);
    const [flash, setFlash] = useState(null);
    const loadRequest = useRef(0);
    const paginationRef = useRef(pagination);
    const listRef = useRef(null);

    const load = useCallback(() => {
        const requestId = ++loadRequest.current;
        const page = paginationRef.current.currentPageNumber || 0;
        setRows(null);
        setError('');
        hiringApi.list(page, '', category).then((data) => {
            if (loadRequest.current !== requestId) return;
            const list = rowsInCategory(Array.isArray(data?.dataset) ? data.dataset : [], category);
            if (list.length === 0 && page > 0) {
                paginationRef.current = { ...paginationRef.current, currentPageNumber: page - 1 };
                setPagination(paginationRef.current);
                setReloadToken((token) => token + 1);
                return;
            }
            const pageInfo = { ...(data || {}) };
            delete pageInfo.dataset;
            paginationRef.current = pageInfo;
            setPagination(pageInfo);
            setRows(list);
            setSelected([]);
        }).catch((loadError) => {
            if (loadRequest.current !== requestId) return;
            setError(hiringErrorMessage(loadError));
        });
    }, [category]);

    useEffect(() => {
        if (canView) load();
    }, [canView, load, reloadToken]);

    useEffect(() => {
        if (!flash || !listRef.current) return;
        const row = listRef.current.querySelector(`[data-applicant-id="${flash.id}"]`);
        if (!row) return;
        row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [rows, flash]);

    const closeLookup = useCallback(() => setLookupOpen(false), []);

    if (!canView) {
        return <div className="errorMessage">You do not have access to this page.</div>;
    }

    const blocks = orderHiringRows(rows || [], sort);

    const toggleSort = (key) => {
        setSort((current) => {
            if (!current || current.key !== key) return { key, desc: false };
            if (!current.desc) return { key, desc: true };
            return null;
        });
    };

    const toggleRow = (checked, id) => {
        setSelected((current) => nextSelection(current, checked, id, (rows || []).length));
    };

    const toggleAll = (checked) => {
        const boxes = listRef.current ? listRef.current.getElementsByClassName('selectableListCheckBox') : [];
        for (let i = 0; i < boxes.length; i += 1) {
            boxes[i].checked = checked;
        }
        setSelected(checked ? ['selectAll'] : []);
    };

    const reload = () => setReloadToken((token) => token + 1);

    const applySaved = (saved) => {
        setRows((current) => {
            if (!Array.isArray(current) || !saved) return current;
            const stillHere = rowsInCategory([saved], category).length > 0;
            if (!stillHere) return current.filter((item) => item.id !== saved.id);
            return current.map((item) => (item.id === saved.id ? { ...item, ...saved } : item));
        });
    };

    const openApplicant = (record, presetPhone = '') => {
        const form = buildApplicantForm(rows || [], reload, record, presetPhone);
        addPopup({
            content: Form,
            popupData: { configuration: form.configuration },
            postSuccessAction: form.postSuccessAction,
            EZCloseAllowed: false,
        });
    };

    const revealApplicant = (hit) => {
        setLookupOpen(false);
        setSort(null);
        setFlash({ id: hit.id, token: Date.now() });
        paginationRef.current = { ...paginationRef.current, currentPageNumber: hit.pageNumber || 0 };
        setCategory(hit.category || 'IN_PROGRESS');
        setReloadToken((token) => token + 1);
    };

    const openDriver = (driver) => {
        const currentId = String(localStorage.getItem('currentCompanyId') || '');
        if (String(driver.companyId) !== currentId) {
            let companies = [];
            try {
                companies = JSON.parse(localStorage.getItem('accessibleCompanies') || '[]');
            } catch (_) {
                companies = [];
            }
            const company = (Array.isArray(companies) ? companies : []).find((item) => String(item.id) === String(driver.companyId));
            if (!company) return;
            setCompany(company);
            window.location.assign(`/drivers/${driver.id}`);
            return;
        }
        navigate(`/drivers/${driver.id}`);
    };

    const confirmRemove = (row) => {
        addPopup({
            content: ConfirmationDialogue,
            EZCloseAllowed: true,
            popupData: {
                confirmationDialogue: true,
                formTitle: 'Remove applicant',
                submitTitle: 'Remove',
                name: 'Confirm',
                message: `Remove ${row.name || 'this applicant'}?`,
                action: (done) => {
                    hiringApi.delete(row.id)
                        .then(() => {
                            reload();
                            done(true);
                        })
                        .catch((removeError) => {
                            addNotification({ message: hiringErrorMessage(removeError), type: 'alert' });
                            done(true);
                        });
                },
                cancelAction: (close) => close(),
            },
        });
    };

    return (
        <>
            <div className={listStyles.pageHeader}>
                <div>Onboarding Hub</div>
                <div className={listStyles.actionsContainer}>
                    <div className={styles.search}>
                        <button type="button" onClick={() => setLookupOpen(true)}>
                            Search By Phone
                        </button>
                    </div>
                    {canManage ? (
                        <div
                            className={`${listStyles.addButton} non-selectable`}
                            onClick={() => openApplicant(null)}
                        >
                            Add Applicant +
                        </div>
                    ) : null}
                </div>
            </div>
            <div className={listStyles.catSelector}>
                {HIRING_CATEGORIES.map((item) => (
                    <div
                        key={item.key}
                        className={category === item.key ? listStyles.selected : ''}
                        onClick={() => {
                            if (item.key === category) return;
                            paginationRef.current = { ...paginationRef.current, currentPageNumber: 0 };
                            setCategory(item.key);
                        }}
                    >
                        {item.label}
                    </div>
                ))}
            </div>
            {rows == null && !error ? <LoadingIcon state height="10rem" /> : null}
            {error ? <div className="errorMessage">{error}</div> : null}
            {rows && rows.length === 0 ? <div className={styles.empty}>No Applications Found</div> : null}
            {rows && rows.length > 0 ? (
                <div className={styles.list} ref={listRef}>
                    <div className={styles.head} style={{ gridTemplateColumns: template }}>
                        <CheckBoxInput
                            checked={false}
                            onChangeAction={{ method: toggleAll, parameter: 'selectAll' }}
                        />
                        {columns.map((column) => (
                            column.sortable === false ? (
                                <div key={column.key} className={`${styles.headLabel} ${column.key === 'actions' ? styles.headLabelCenter : ''}`}>{column.label}</div>
                            ) : (
                                <button
                                    key={column.key}
                                    type="button"
                                    className={styles.headButton}
                                    onClick={() => toggleSort(column.key)}
                                >
                                    <span>{column.label}</span>
                                    <span className={sort?.key === column.key ? styles.arrowOn : styles.arrow}>
                                        {sort?.key === column.key && sort.desc ? <HiArrowNarrowDown /> : <HiArrowNarrowUp />}
                                    </span>
                                </button>
                            )
                        ))}
                        <div />
                    </div>
                    {blocks.map((block) => (
                        <div
                            key={block.rows.map((row) => row.id).join('-')}
                            className={block.kind === 'pair' ? styles.pair : styles.single}
                        >
                            {block.rows.map((row) => (
                                <div
                                    key={flash?.id === row.id ? `${row.id}-${flash.token}` : row.id}
                                    data-applicant-id={row.id}
                                    className={flash?.id === row.id ? `${styles.row} ${styles.highlight}` : styles.row}
                                    style={{ gridTemplateColumns: template }}
                                >
                                    <div className={styles.check} onClick={(event) => event.stopPropagation()}>
                                        <CheckBoxInput
                                            checked={false}
                                            className="selectableListCheckBox"
                                            onChangeAction={{ method: toggleRow, parameter: row.id }}
                                        />
                                    </div>
                                    {columns.map((column) => {
                                        if (column.key === 'actions') {
                                            return (
                                                <div
                                                    key={column.key}
                                                    className={`${activitiesStyles.latestActionsContainer} ${styles.actionIcon}`}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        openActivitiesPopup({
                                                            targetType: 'POTENTIAL_DRIVER',
                                                            objectId: row.id,
                                                            titleLabel: row.name || '',
                                                            popupKind: 'potentialDriverActivities',
                                                        });
                                                    }}
                                                >
                                                    <MdHistory />
                                                </div>
                                            );
                                        }
                                        if (column.key === 'status' || column.key === 'insuranceStatus') {
                                            return (
                                                <div key={column.key} className={styles.cell}>
                                                    <BadgeField
                                                        row={row}
                                                        field={column.key}
                                                        options={column.key === 'status' ? STATUS_OPTIONS : INSURANCE_OPTIONS}
                                                        canManage={canManage}
                                                        onUpdated={applySaved}
                                                    />
                                                </div>
                                            );
                                        }
                                        if (column.key === 'comingDate') {
                                            return (
                                                <div key={column.key} className={styles.cell}>
                                                    <ComingDateField
                                                        row={row}
                                                        canManage={canManage}
                                                        onUpdated={applySaved}
                                                    />
                                                </div>
                                            );
                                        }
                                        if (column.key === 'files') {
                                            return (
                                                <div key={column.key} className={`${styles.cell} ${styles.fileCell}`}>
                                                    <FilesField
                                                        row={row}
                                                        canManage={canManage}
                                                        onUpdated={applySaved}
                                                    />
                                                </div>
                                            );
                                        }
                                        return (
                                            <div key={column.key} className={`${styles.cell} ${column.key === 'companyName' ? styles.ellipsis : ''}`}>
                                                {cellText(row, column.key, block.kind === 'pair')}
                                            </div>
                                        );
                                    })}
                                    <div className={styles.menu} onClick={(event) => event.stopPropagation()}>
                                        {canManage ? (
                                            <ThreeDotsButton
                                                position="relative"
                                                onEdit={() => openApplicant(row)}
                                                onRemove={() => confirmRemove(row)}
                                            />
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            ) : null}
            {lookupOpen ? (
                <PhoneLookup
                    canManage={canManage}
                    canViewDrivers={canViewDrivers}
                    onClose={closeLookup}
                    onRevealApplicant={revealApplicant}
                    onOpenDriver={openDriver}
                    onAddApplicant={(phone) => {
                        setLookupOpen(false);
                        openApplicant(null, phone);
                    }}
                />
            ) : null}
            {rows ? (
                <ListViewFooter
                    paginationData={{
                        data: pagination,
                        setPaginationData: (update) => {
                            const next = update(paginationRef.current);
                            paginationRef.current = next;
                            setPagination({ ...next });
                        },
                    }}
                    resultCount={rows.length}
                    requestReload={() => setReloadToken((token) => token + 1)}
                />
            ) : null}
        </>
    );
}
